import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Chip, IconButton, TextField, CircularProgress, Avatar, Tooltip,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import { Page, Card } from '../ui';

const PLATFORM = {
  fb: { label: 'Facebook', color: '#1877f2' },
  ig: { label: 'Instagram', color: '#e1306c' },
};

export default function CommentsPage() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const loadFeed = useCallback(() => {
    setLoading(true);
    axios.get('/comments')
      .then((r) => setComments(r.data.comments || []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // «Обновить»: подтянуть страницы из Facebook, затем перечитать ленту.
  const refresh = async () => {
    setLoading(true);
    try { await axios.post('/pages/sync'); } catch (e) { /* ignore */ }
    loadFeed();
  };

  const hide = async (c) => {
    try {
      await axios.post(`/comments/${c.id}/hide`, null, { params: { page_id: c.page_id, platform: c.platform, hidden: !c.is_hidden } });
      setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_hidden: !x.is_hidden } : x)));
    } catch (e) { window.alert('Не удалось изменить комментарий'); }
  };
  const del = async (c) => {
    try {
      await axios.delete(`/comments/${c.id}`, { params: { page_id: c.page_id } });
      setComments((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) { window.alert('Не удалось удалить (Instagram разрешает удалять только свои комментарии)'); }
  };
  const sendReply = async (c) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`/comments/${c.id}/reply`, { message: replyText.trim() }, { params: { page_id: c.page_id, platform: c.platform } });
      setReplyTo(null); setReplyText('');
    } catch (e) { window.alert('Не удалось отправить ответ'); }
  };

  return (
    <Page
      title="Комментарии"
      subtitle="Единая лента со всех Facebook-страниц и привязанных Instagram"
      action={<Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={refresh} disabled={loading}>{loading ? 'Загрузка…' : 'Обновить'}</Button>}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : comments.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Комментариев нет. Нажми «Обновить», чтобы подтянуть страницы и ленту.</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {comments.map((c) => {
            const pl = PLATFORM[c.platform] || PLATFORM.fb;
            return (
              <Card key={c.id} sx={{ p: 2, opacity: c.is_hidden ? 0.55 : 1 }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: pl.color, fontSize: 14 }}>{(c.page_name || '?')[0]}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{c.page_name || '—'}</Typography>
                      <Chip size="small" label={pl.label} sx={{ height: 18, fontSize: 10, bgcolor: `${pl.color}22`, color: pl.color }} />
                      {c.is_hidden && <Chip size="small" label="Скрыт" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: 'auto' }}>{c.created_time ? new Date(c.created_time).toLocaleString('ru-RU') : ''}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, color: 'text.primary', whiteSpace: 'pre-wrap' }}>{c.message}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.3 }}>— {c.author}</Typography>
                    {replyTo === c.id && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <TextField size="small" fullWidth placeholder="Ответ от имени страницы…" value={replyText} onChange={(e) => setReplyText(e.target.value)} />
                        <Button variant="contained" onClick={() => sendReply(c)}>Отправить</Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title={c.is_hidden ? 'Показать' : 'Скрыть'}>
                      <IconButton size="small" onClick={() => hide(c)}>{c.is_hidden ? <VisibilityRoundedIcon fontSize="small" /> : <VisibilityOffRoundedIcon fontSize="small" />}</IconButton>
                    </Tooltip>
                    <Tooltip title="Ответить">
                      <IconButton size="small" onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(''); }}><ReplyRoundedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <IconButton size="small" onClick={() => del(c)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </Page>
  );
}
